import type { MemberAmounts, ProposalResponse, TierBreakdown } from '@/types/waterfall'
import type { CalculatorInputs } from '@/utils/waterfallCalculator'

export const CALCULATOR_CACHE_KEY = 'waterfall_calculator_cache_v2'
export const CALCULATOR_CACHE_TTL_MS = 10 * 60 * 60 * 1000

export interface CalculatorCacheSnapshot {
  inputs: CalculatorInputs
  parsedAmount: number
  expectedLastNum: number
  result: ProposalResponse
  proposalGrid: TierBreakdown
  vesting: import('@/types/waterfall').ProposalVestingItem[]
}

export interface CalculatorCachePayload {
  savedAt: number
  snapshot: CalculatorCacheSnapshot
  actualTotals: MemberAmounts
  viewMode: 'table' | 'flow'
}

export function isCalculatorCacheValid(savedAt: number, now = Date.now()): boolean {
  return now - savedAt < CALCULATOR_CACHE_TTL_MS
}

export function loadCalculatorCache(): CalculatorCachePayload | null {
  try {
    const raw = localStorage.getItem(CALCULATOR_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CalculatorCachePayload & { viewMode?: string }
    if (!parsed?.savedAt || !parsed.snapshot) return null
    if (!isCalculatorCacheValid(parsed.savedAt)) {
      localStorage.removeItem(CALCULATOR_CACHE_KEY)
      return null
    }
    if ((parsed.viewMode as string | undefined) === 'sankey') {
      parsed.viewMode = 'flow'
    }
    return parsed
  } catch {
    localStorage.removeItem(CALCULATOR_CACHE_KEY)
    return null
  }
}

export function saveCalculatorCache(payload: Omit<CalculatorCachePayload, 'savedAt'>): void {
  const full: CalculatorCachePayload = { ...payload, savedAt: Date.now() }
  localStorage.setItem(CALCULATOR_CACHE_KEY, JSON.stringify(full))
}

export function clearCalculatorCache(): void {
  localStorage.removeItem(CALCULATOR_CACHE_KEY)
}
